<template>
  <div
    class="playlist-layer playlist-item timeline-segment"
    data-component-type="segment"
    :class="{'editing': isEditing}"
    v-click-outside="saveName"
  >
    <div class="layer-info" @click.stop="!isEditingName && onClickTimelineItem()" @dblclick.prevent="!isEditingName ? toggleCollapse() : null">
      <button
        class="drag-handle tb-btn tb-btn-link sm low-em"
        title="Rearrange"
      >
        <span class="fas fa-grip-vertical"></span>
      </button>

      <div class="layer-title">
        <button
          class="tb-btn tb-btn-link sm low-em collapse-btn"
          :class="isCollapsed ? 'collapse-btn--rotated' : ''"
          :title="isCollapsed ? 'Expand' : 'Collapse'"
          @click.stop="toggleCollapse"
        >
          <span class="fas fa-chevron-down"></span>
        </button>

        <div class="layer-name--editable gap-0 timeline-segment__name">
          <div class="layer-name--editable__main timeline-segment__name-main">
            <span v-if="!isEditingName" class="fak fa-segment"></span>
            <div
              v-if="!isEditingName"
              :title="title"
              class="title-text timeline-segment__title-text"
              @dblclick.stop="editName"
            >
              {{ title }}
            </div>
            <span v-if="!isEditingName" @click.stop="editName" class="fas fa-pencil-alt"></span>
          </div>

          <div v-if="!isEditingName" class="description-text layer-name--editable__count timeline-segment__count">
            {{ itemCount }}
          </div>

          <div v-if="isEditingName" class="layer-name--editable__edit layer-name--editable__edit--segment timeline-segment__edit">
            <input
              type="text"
              class="layer-name__input min-w-[12rem] appearance-none"
              data-test="segment-title-input"
              v-model="name"
              autofocus
              ref="nameInput"
              @keyup.enter="saveName"
              @keyup.esc="cancelEditingName"
            />
            <span
              @click.stop="saveName"
              class="fas fa-check layer-name__action layer-name__action--accept"
              data-test="segment-title-accept"
            />
            <span
              @click.stop="cancelEditingName"
              class="fas fa-times layer-name__action layer-name__action--cancel"
              data-test="segment-title-cancel"
            />
          </div>
        </div>
      </div>

      <div class="layer-description capitalize margin-left-xxs">
      </div>

      <dropdown position="left" class="more-actions margin-left-xxs" @dblclick.stop>
        <template #trigger>
          <button
            class="tb-btn tb-btn-text sm medium-em"
            title="More actions"
            @dblclick.stop
          >
            <span class="fas fa-ellipsis-v"></span>
          </button>
        </template>
        <template #dropdown-body>
          <button
            class="dropdown-list-item"
            v-on:click="duplicateItem"
          >
            <span class="dropdown-list-item-label">Duplicate in Playlist</span>
          </button>

          <button
            v-if="canDuplicateInGroup"
            class="dropdown-list-item"
            v-on:click="duplicateItemInGroup"
          >
            <span class="dropdown-list-item-label">Duplicate in Segment</span>
          </button>

          <button
            v-if="canMergeUp"
            class="dropdown-list-item"
            @click="mergeUp"
            title="Merge with above item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Up</span>
          </button>

          <button
            v-if="canMergeDown"
            class="dropdown-list-item"
            @click="mergeDown"
            title="Merge with below item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Down</span>
          </button>

          <hr>

          <button
            class="dropdown-list-item"
            v-on:click="removeItem"
          >
            <span class="dropdown-list-item-label warning">
              Remove
            </span>
          </button>
        </template>
      </dropdown>
    </div>

    <div
      v-if="!isCollapsed"
      class="position-relative"
      :class="item.posts.length ? '' : 'padding-y-sm '"
    >
      <draggable
        v-model="item.posts"
        class="playlist-draggable playlist-draggable--nested tgbDropArea tgbDropArea--graphics"
        :class="[{'empty': !item.posts.length},'zoom-' + zoom]"
        :move="onSegmentMove"
        v-bind="{
          group: { name: 'segmentGroup', put: allowedSourceGroups },
          animation: 200,
          touchStartThreshold: 10,
        }"
        handle=".drag-handle"
        revert-on-spill="true"
        data-test="producer-timeline-segment-droparea"
        @change="onChangeGroup"
      >
        <component
          v-for="(child, childIndex) in children"
          :key="child.timeline_id"
          :data-timeline-id="child.timeline_id"
          class="playlist-layer--nested"
          :is="getType(child)"
          :item="child"
          :index="childIndex"
          :prev="childIndex > 0 ? children[childIndex - 1] : null"
          :next="childIndex < children.length - 1 ? children[childIndex + 1] : null"
          :in-container="true"
          :container-timeline-id="item.timeline_id"
        />

        <div class="tgbDropAreaHelp" v-if="!item.posts.length">
          <p>Drag and drop items here</p>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import ConduitEventsService from '../../../services/conduitEventsService';

import TimelineMixins from '../../../mixins/timeline-mixins';
import Dropdown from '../../../shared/dropdown/Dropdown.vue';

import TimelinePanelComponent from '../panel/TimelinePanel.vue';
import TimelineGraphicComponent from '../graphic/TimelineGraphic.vue';
import TimelineGraphicsComponent from '../graphic/TimelineGraphics.vue';
import TimelineMultipostComponent from '../multipost/TimelineMultipost.vue';
import TimelineThemeComponent from '../theme/TimelineTheme.vue';
import TimelineAsset from '../TimelineAsset.vue';
import TimelinePoll from '../TimelinePoll.vue';
import TimelineMarker from '../TimelineMarker.vue';

const dispatchEvent = (event) => {
  const evt = document.createEvent('Event');
  evt.initEvent(event, true, true);
  document.dispatchEvent(evt);
};

export default {
  name: 'TimelineSegmentComponent',
  mixins: [TimelineMixins],

  data() {
    return {
      isEditingName: false,
      isCollapsed: false,
      name: '',
    };
  },

  components: {
    Dropdown,
    theme: TimelineThemeComponent,
    graphic: TimelineGraphicComponent,
    graphics: TimelineGraphicsComponent,
    panel: TimelinePanelComponent,
    multi: TimelineMultipostComponent,
    asset: TimelineAsset,
    poll: TimelinePoll,
    'timeline-marker': TimelineMarker,
  },

  computed: {
    allowedSourceGroups() {
      const allowedTypes = ['panel', 'graphic', 'graphics', 'asset', 'poll', 'theme', 'component', 'marker'];
      return (to, from) => {
        const sourceGroup = from?.options?.group?.name;
        const dragType = this.$store.state.drag.type;

        // Nested drags (segment -> segment) may not populate drag.type reliably.
        // If the source is another segment group, allow the drop and let onChangeGroup
        // validate the actual element payload.
        if (sourceGroup === 'segmentGroup') {
          return true;
        }

        return allowedTypes.includes(dragType);
      };
    },

    children() {
      return this.item.posts || [];
    },

    itemCount() {
      return `${this.children.length} Item${this.children.length === 1 ? '' : 's'}`;
    },

    title() {
      return this.item.title;
    },
  },
  methods: {
    /**
     * Validate drops before Sortable commits the move. Rejecting an invalid
     * drop here (instead of letting it land and then reverting) stops Sortable
     * from physically moving the DOM node and leaving a stray element behind
     * that Vue won't clean up. Segments only accept timeline items that carry
     * a layout (never other segments, posts, or malformed entries).
     * @param {Object} evt - Sortable move event carrying draggedContext
     * @returns {Boolean} Whether the drop is allowed
     */
    onSegmentMove(evt) {
      const element = evt?.draggedContext?.element;

      if (!element?.layout || element.layout === 'segment') {
        return false;
      }

      return true;
    },

    getType(item) {
      const { layout } = item;

      if (['panel', 'graphic', 'graphics', 'asset', 'poll'].includes(layout)) {
        return item.layout;
      }

      if (layout === 'marker') {
        return 'timeline-marker';
      }

      const isMultiPostLayout = (
        ['grid', 'socialburst', 'waterfall', 'carousel', 'mural', 'upnext'].includes(layout)
        || (item.posts?.[0]?.type === 'tagboard')
      );

      return isMultiPostLayout ? 'multi' : 'theme';
    },

    async onChangeGroup(e) {
      if (e.added) {
        const { element, newIndex } = e.added;
        const { group } = this.$store.state.drag;

        // Segments only accept timeline items (objects with a layout), not posts/tagboards.
        if (!element?.layout || element.layout === 'segment') {
          this.item.posts = this.item.posts.filter((child) => child?.timeline_id !== element?.timeline_id);
          return;
        }

        dispatchEvent('clearTabSelection');

        if (group && group.length) {
          this.$store.dispatch('replaceGroupItem', {
            groupIndex: this.index,
            index: newIndex,
            items: group,
          });
        } else {
          // Single-item drag: deterministically place the dragged item in
          // THIS segment and nowhere else. Nested-list drags can otherwise
          // leave it duplicated (root + segment) or drop it entirely when
          // the DOM and model desync; reconciling by timeline_id fixes any
          // such corruption.
          this.$store.dispatch('moveTimelineItemsToContainer', {
            items: [element],
            containerTimelineId: this.item.timeline_id,
            index: newIndex,
          });
        }

        ConduitEventsService.track(
          'producer.dragged.items-to-segment',
          {
            production_id: this.production._id,
            count: group?.length || 1,
            segment: this.item,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
        );
      }

      if (e.removed || e.moved) {
        // Drop null/empty entries vuedraggable can leave behind when an
        // operation is reverted; empty `{}` objects are corrupt and break
        // consumers that read layout/fields/meta off nested items.
        this.$store.dispatch('cleanContainerPosts', {
          containerTimelineId: this.item.timeline_id,
        });
      }

      await this.$store.dispatch('getTimelineItems');
      this.$store.dispatch('trackSportradarInGameElement');
      this.$store.dispatch('updateTimeline', { force: true });
      this.cleanupStrayNodes();

      if (this.isCurrentSampleLayout) {
        this.$store.dispatch('updatePreviewTimeline', { force: true });
      }
    },

    /**
     * Remove any DOM nodes Sortable left inside this segment's dropzone that
     * no longer correspond to a post. Nested-list drags (root <-> segment)
     * can leave a stray moved node behind after a reverted/desynced drop, and
     * because Vue doesn't manage those nodes it never removes them.
     */
    cleanupStrayNodes() {
      this.$nextTick(() => {
        const container = this.$el?.querySelector('.playlist-draggable--nested');
        if (!container) {
          return;
        }

        const validIds = new Set((this.item.posts || []).map((post) => post?.timeline_id));

        Array.from(container.children).forEach((child) => {
          const timelineId = child.getAttribute?.('data-timeline-id');
          if (timelineId && !validIds.has(timelineId)) {
            child.remove();
          }
        });
      });
    },

    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    editName() {
      this.name = this.item.title;
      this.isEditingName = true;

      this.$nextTick(() => {
        this.$refs.nameInput.focus();
        this.$refs.nameInput.select();
      });
    },

    cancelEditingName() {
      this.isEditingName = false;
    },

    saveName() {
      if (!this.isEditingName) { return; }

      this.isEditingName = false;

      if (!this.name || !this.name.trim()) { return; }

      this.item.title = this.name.trim();

      this.$store.dispatch('updateTimeline');

      ConduitEventsService.track(
        'producer.renamed.segment',
        {
          production_id: this.production._id,
          name: this.item.title,
        },
        {
          ownerId: this.production.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );
    },
  },
};
</script>

<style scoped lang="scss">
.layer-name__input {
  padding: 0.45rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.08);
  color: #e1e1e1;
  box-shadow: none;
  outline: none;

  &:focus {
    border-color: rgba(255, 255, 255, 0.2);
    background-color: rgba(255, 255, 255, 0.12);
  }
}

.layer-name__action {
  cursor: pointer;
  font-size: 0.875rem;
}

.layer-name__action--accept {
  color: #00d483;
}

.layer-name__action--cancel {
  color: #e65447;
}
</style>
